/**
 * <product-gallery>
 *
 * Native scroll-snap gallery for the product page. The layout itself is driven
 * entirely by CSS (see product-gallery.css); this component only keeps the
 * state in sync: active thumbnail, arrows, slide indicator, keyboard support
 * and the variant -> media selection coming from global.js.
 *
 * Public API:
 *   gallery.selectMedia(mediaId)  // scrolls to the media of the given id
 */
(function () {
	"use strict";

	// How long the track has to stay still before the active slide is committed.
	const SETTLE_DELAY = 150;

	// Matches the desktop breakpoint used throughout product-gallery.css.
	const DESKTOP_MEDIA = "(min-width: 990px)";

	// Bounds for the hand-rolled swipe animation, in milliseconds.
	const SWIPE_MIN_DURATION = 300;
	const SWIPE_MAX_DURATION = 700;

	// How long the slides must stop changing before a variant change is acted
	// on, and the longest we will wait for a variant-image app to finish.
	const VARIANT_SETTLE_DELAY = 120;
	const VARIANT_MAX_WAIT = 700;

	class ProductGallery extends HTMLElement {
		constructor() {
			super();

			this.activeIndex = 0;
			this.scrollFrame = null;
			this.settleTimer = null;
			this.animationFrame = null;
			this.snapSuspended = false;
			this.renderedActive = null;
			this.renderedFirst = null;
			this.lastWidth = window.innerWidth;

			this.onScroll = this.onScroll.bind(this);
			this.onScrollEnd = this.onScrollEnd.bind(this);
			this.onResize = this.onResize.bind(this);
			this.onKeydown = this.onKeydown.bind(this);
		}

		connectedCallback() {
			this.cacheElements();

			if (!this.track || this.slides.length === 0) return;

			// connectedCallback also fires when the node is moved (quick view),
			// so listeners are always rebound from scratch.
			if (this.controller) this.controller.abort();
			this.controller = new AbortController();

			this.bindEvents(this.controller.signal);
			this.setInitialSlide();
			this.syncVisibleMedia();

			// Variant-image apps change the main slides, not our separate thumb rail.
			this.mediaObserver = new MutationObserver(() => this.syncVisibleMedia());
			this.mediaObserver.observe(this.track, {
				attributes: true,
				attributeFilter: ["class", "style", "hidden"],
				subtree: true,
			});
		}

		disconnectedCallback() {
			if (this.controller) this.controller.abort();
			if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
			if (this.settleTimer) clearTimeout(this.settleTimer);
			this.cancelScrollAnimation();
			this.cancelVariantWatch();
			if (this.mediaObserver) this.mediaObserver.disconnect();
			this.scrollFrame = null;
			this.settleTimer = null;
		}

		cacheElements() {
			this.track = this.querySelector("[data-gallery-track]");
			this.slides = Array.from(this.querySelectorAll("[data-gallery-slide]"));
			this.thumbs = Array.from(this.querySelectorAll("[data-gallery-thumb]"));
			this.thumbScroller = this.querySelector("[data-gallery-thumbs-scroller]");
			this.prevButton = this.querySelector("[data-gallery-prev]");
			this.nextButton = this.querySelector("[data-gallery-next]");
			this.dots = Array.from(this.querySelectorAll("[data-gallery-dot]"));
			this.dotsTrack = this.querySelector("[data-gallery-dots-track]");
		}

		bindEvents(signal) {
			this.track.addEventListener("scroll", this.onScroll, {
				passive: true,
				signal: signal,
			});
			this.track.addEventListener("scrollend", this.onScrollEnd, {
				passive: true,
				signal: signal,
			});
			this.addEventListener("keydown", this.onKeydown, { signal: signal });
			window.addEventListener("resize", this.onResize, { signal: signal });

			// A finger on the track always wins over a running animation.
			const stopAnimation = () => this.cancelScrollAnimation();
			this.track.addEventListener("pointerdown", stopAnimation, {
				passive: true,
				signal: signal,
			});
			this.track.addEventListener("touchstart", stopAnimation, {
				passive: true,
				signal: signal,
			});
			this.track.addEventListener("wheel", stopAnimation, {
				passive: true,
				signal: signal,
			});

			this.thumbs.forEach((thumb, index) => {
				thumb.addEventListener(
					"click",
					(event) => {
						event.preventDefault();
						this.goTo(index, "smooth");
					},
					{ signal: signal },
				);
			});

			this.dots.forEach((dot, index) => {
				dot.addEventListener(
					"click",
					(event) => {
						event.preventDefault();
						this.goTo(index, "smooth");
					},
					{ signal: signal },
				);
			});

			if (this.prevButton) {
				this.prevButton.addEventListener("click", () => this.step(-1), {
					signal: signal,
				});
			}

			if (this.nextButton) {
				this.nextButton.addEventListener("click", () => this.step(1), {
					signal: signal,
				});
			}
		}

		/**
		 * The track only scrolls in the carousel layouts. In the stacked desktop
		 * layout CSS switches it to `overflow: visible`, so the computed style is
		 * the single source of truth and stays correct across breakpoints.
		 */
		get isCarousel() {
			if (!this.track) return false;
			return window.getComputedStyle(this.track).overflowX !== "visible";
		}

		get isDesktop() {
			return window.matchMedia(DESKTOP_MEDIA).matches;
		}

		/**
		 * First slide that actually renders. A variant-image app may have hidden
		 * the leading slides, and scrolling to a display:none slide goes nowhere.
		 */
		firstRenderedIndex() {
			const index = this.slides.findIndex(
				(slide) => slide.getClientRects().length > 0,
			);
			return index === -1 ? 0 : index;
		}

		visibleIndices() {
			return this.slides.reduce((indices, slide, index) => {
				if (slide.getClientRects().length > 0) indices.push(index);
				return indices;
			}, []);
		}

		syncVisibleMedia() {
			const visible = this.visibleIndices();
			const visibleSet = new Set(visible);
			const changed = this.thumbs.some((thumb, index) =>
				thumb.hidden === visibleSet.has(index),
			);
			if (!changed) return;

			this.thumbs.forEach((thumb, index) => {
				thumb.hidden = !visibleSet.has(index);
			});
			this.dots.forEach((dot, index) => {
				dot.hidden = !visibleSet.has(index);
			});
			this.renderedActive = null;
			this.cancelScrollAnimation();

			if (this.isCarousel && visible.length) {
				const preferred = this.slides.findIndex(
					(slide) => slide.dataset.galleryMediaId === this.preferredMediaId,
				);
				const next = this.isDesktop && visibleSet.has(preferred)
					? preferred
					: visibleSet.has(this.activeIndex) ? this.activeIndex : visible[0];
				this.goTo(next, "auto");
			}
			this.updateArrows();
		}

		cancelVariantWatch() {
			if (this.variantObserver) {
				this.variantObserver.disconnect();
				this.variantObserver = null;
			}
			if (this.variantTimer) {
				clearTimeout(this.variantTimer);
				this.variantTimer = null;
			}
		}

		/**
		 * Mobile variant change: land on the first media of the NEW variant.
		 *
		 * Variant-image apps re-filter the slides asynchronously, a moment after
		 * the picker fires. Reading the gallery straight away would therefore
		 * pick the outgoing variant's first media, which is what makes it look
		 * like nothing happened. So wait until the track stops changing — or
		 * until a short grace period passes, for shops with no such app — and
		 * only then take the first slide that is actually on show.
		 */
		goToVariantStart() {
			this.cancelVariantWatch();

			const startedAt = Date.now();

			const run = () => {
				// Disarm first: the scroll below mutates the track itself.
				this.cancelVariantWatch();
				this.goTo(this.firstRenderedIndex(), "smooth");
			};

			const bump = () => {
				if (this.variantTimer) clearTimeout(this.variantTimer);

				if (Date.now() - startedAt >= VARIANT_MAX_WAIT) {
					run();
					return;
				}

				this.variantTimer = setTimeout(run, VARIANT_SETTLE_DELAY);
			};

			if (typeof MutationObserver === "function") {
				this.variantObserver = new MutationObserver(bump);
				this.variantObserver.observe(this.track, {
					attributes: true,
					attributeFilter: ["class", "style", "hidden"],
					subtree: true,
				});
			}

			bump();
		}

		setInitialSlide() {
			const initialId = this.getAttribute("data-initial-media-id");
			let index = 0;

			if (initialId) {
				const found = this.slides.findIndex(
					(slide) => slide.dataset.galleryMediaId === initialId,
				);
				if (found > -1) index = found;
			}

			this.setActive(index, { animate: false, pauseMedia: false });

			if (index > 0 && this.isCarousel) this.goTo(index, "auto");

			this.updateArrows();
		}

		onScroll() {
			// Arrows track the scroll extents and the dot indicator follows the
			// finger, so both update live; only the discrete active slide waits
			// for the track to settle.
			if (!this.scrollFrame) {
				this.scrollFrame = requestAnimationFrame(() => {
					this.scrollFrame = null;
					this.updateArrows();

					// A commit can land between scheduling this frame and running
					// it (`scrollend` fires ahead of the queued frame). No pending
					// settle means the track is already at rest, so leave the strip
					// where the commit put it rather than re-entering tracking.
					if (this.settleTimer === null) return;

					if (this.dots.length > 0 && this.isCarousel) {
						this.setDotsTracking(true);
						this.renderDots(this.fractionalIndex());
					}
				});
			}

			this.scheduleSettle();
		}

		onScrollEnd() {
			if (this.settleTimer) {
				clearTimeout(this.settleTimer);
				this.settleTimer = null;
			}
			this.commitActiveSlide();
		}

		/**
		 * The active slide is committed only once the track comes to rest.
		 * Updating it on every scroll frame makes the indicator and thumbnails
		 * flick through slides the shopper never actually lands on, which reads
		 * as a wrong number mid-swipe. `scrollend` handles it where supported,
		 * this timer is the fallback everywhere else.
		 */
		scheduleSettle() {
			if (this.settleTimer) clearTimeout(this.settleTimer);

			this.settleTimer = setTimeout(() => {
				this.settleTimer = null;
				this.commitActiveSlide();
			}, SETTLE_DELAY);
		}

		commitActiveSlide() {
			if (!this.isCarousel) return;

			// Hand the dot strip back to its CSS transition and let it land on
			// the exact whole-slide position.
			this.setDotsTracking(false);

			const index = this.closestSlideIndex();
			if (index !== this.activeIndex) {
				this.setActive(index);
			} else {
				this.renderDots(index);
			}

			this.updateArrows();
		}

		onResize() {
			if (window.innerWidth === this.lastWidth) return;
			this.lastWidth = window.innerWidth;

			if (this.isCarousel) this.goTo(this.activeIndex, "auto");
			this.updateArrows();
		}

		onKeydown(event) {
			if (!this.isCarousel) return;

			if (event.key === "ArrowRight") {
				event.preventDefault();
				this.step(1);
			} else if (event.key === "ArrowLeft") {
				event.preventDefault();
				this.step(-1);
			}
		}

		/**
		 * Matches on slide centres rather than left edges: with the mobile peek
		 * layout the slides are narrower than the track and the last one rests
		 * end-aligned, so a left-edge comparison picks the wrong slide near the
		 * hand-over point and at the end of the track.
		 */
		closestSlideIndex() {
			const trackRect = this.track.getBoundingClientRect();
			const trackCentre = trackRect.left + trackRect.width / 2;
			let bestIndex = 0;
			let bestDistance = Infinity;

			this.slides.forEach((slide, index) => {
				if (slide.getClientRects().length === 0) return;
				const rect = slide.getBoundingClientRect();
				const distance = Math.abs(rect.left + rect.width / 2 - trackCentre);
				if (distance < bestDistance) {
					bestDistance = distance;
					bestIndex = index;
				}
			});

			return bestIndex;
		}

		step(direction) {
			const visible = this.visibleIndices();
			const current = visible.indexOf(this.activeIndex);
			const next = visible[current === -1 ? 0 : current + direction];
			if (next !== undefined) this.goTo(next, "smooth");
		}

		goTo(index, behavior) {
			const slide = this.slides[index];
			if (!slide || slide.getClientRects().length === 0) return;

			if (!this.isCarousel) {
				this.setActive(index);
				this.scrollPageToSlide(slide);
				return;
			}

			const offset =
				slide.getBoundingClientRect().left -
				this.track.getBoundingClientRect().left;
			const maxScroll = this.track.scrollWidth - this.track.clientWidth;
			const left = Math.max(
				0,
				Math.min(this.track.scrollLeft + offset, maxScroll),
			);

			if (behavior === "auto") {
				this.cancelScrollAnimation();
				const previous = this.track.style.scrollBehavior;
				this.track.style.scrollBehavior = "auto";
				this.track.scrollLeft = left;
				this.track.style.scrollBehavior = previous;
			} else {
				this.animateScrollTo(left, () => this.updateArrows());
			}

			this.setActive(index);
			this.updateArrows();
		}

		cancelScrollAnimation() {
			if (this.animationFrame) cancelAnimationFrame(this.animationFrame);
			this.animationFrame = null;
			this.releaseSnap();
		}

		releaseSnap() {
			if (!this.snapSuspended) return;
			this.track.style.scrollSnapType = "";
			this.track.style.scrollBehavior = "";
			this.snapSuspended = false;
		}

		/**
		 * Eased scroll that reads like a swipe.
		 *
		 * The browser's own smooth scrolling covers a long distance almost
		 * instantly, which looks like a teleport, so the motion is driven here
		 * with a duration that grows with the distance. Snapping and the CSS
		 * `scroll-behavior: smooth` are both suspended while it runs: with
		 * `scroll-snap-type: mandatory` every intermediate scrollLeft would be
		 * yanked to the nearest snap point, and the CSS smooth behaviour would
		 * animate each of our own frame-by-frame assignments on top.
		 */
		animateScrollTo(left, onDone) {
			this.cancelScrollAnimation();

			const from = this.track.scrollLeft;
			const delta = left - from;

			if (Math.abs(delta) < 1) {
				if (onDone) onDone();
				return;
			}

			const duration = Math.min(
				SWIPE_MAX_DURATION,
				Math.max(SWIPE_MIN_DURATION, 200 + Math.abs(delta) * 0.25),
			);

			this.track.style.scrollSnapType = "none";
			this.track.style.scrollBehavior = "auto";
			this.snapSuspended = true;

			const startTime = performance.now();

			const tick = (now) => {
				const progress = Math.min(1, (now - startTime) / duration);
				const eased = 1 - Math.pow(1 - progress, 3);

				this.track.scrollLeft = from + delta * eased;

				if (progress < 1) {
					this.animationFrame = requestAnimationFrame(tick);
					return;
				}

				this.animationFrame = null;
				this.releaseSnap();
				if (onDone) onDone();
			};

			this.animationFrame = requestAnimationFrame(tick);
		}

		/**
		 * Continuous position of the track between slides, e.g. 3.4 when the
		 * shopper is 40% of the way from the fourth media to the fifth. Uses
		 * offsetLeft, which is layout based and so unaffected by the scroll.
		 */
		fractionalIndex() {
			const slides = this.slides.filter((slide) => slide.getClientRects().length > 0);
			if (slides.length < 2) return 0;

			const base = slides[0].offsetLeft;
			const x = this.track.scrollLeft;

			for (let i = 0; i < slides.length - 1; i += 1) {
				const a = slides[i].offsetLeft - base;
				const b = slides[i + 1].offsetLeft - base;

				if (b <= a) continue;
				if (x < b) {
					const first = this.slides.indexOf(slides[i]);
					const next = this.slides.indexOf(slides[i + 1]);
					return x <= a ? first : first + (next - first) * (x - a) / (b - a);
				}
			}

			return this.slides.indexOf(slides[slides.length - 1]);
		}

		setDotsTracking(isTracking) {
			if (!this.dotsTrack) return;
			this.dotsTrack.classList.toggle("is-tracking", isTracking);
		}

		/** Stacked layout: only pull the page when the media is fully off screen. */
		scrollPageToSlide(slide) {
			const rect = slide.getBoundingClientRect();
			if (rect.bottom > 0 && rect.top < window.innerHeight) return;

			window.scrollTo({
				top: window.pageYOffset + rect.top - 24,
				behavior: "smooth",
			});
		}

		setActive(index, options) {
			const settings = options || {};
			this.activeIndex = index;

			this.slides.forEach((slide, i) => {
				slide.classList.toggle("is-active", i === index);
			});

			this.thumbs.forEach((thumb, i) => {
				const isActive = i === index;
				thumb.classList.toggle("is-active", isActive);
				thumb.setAttribute("aria-current", isActive ? "true" : "false");
			});

			this.renderDots(index);
			this.scrollThumbIntoView(index, settings.animate === false ? "auto" : "smooth");

			if (settings.pauseMedia !== false && typeof window.pauseAllMedia === "function") {
				window.pauseAllMedia();
			}
		}

		/**
		 * Keeps a fixed-size window of dots around the active one, so the
		 * indicator stays the same small size whether the product has 3 media
		 * or 40. Dots at the window edge shrink, the rest collapse away.
		 *
		 * `position` may be fractional: while the shopper swipes it is the live
		 * scroll position, so the strip glides with the finger instead of
		 * hopping once the slide is selected. Settling passes a whole number,
		 * which lands the strip on exactly the same offset.
		 */
		renderDots(position) {
			if (this.dots.length === 0) return;

			const visible = this.visibleIndices();
			const total = visible.length;
			if (!total) return;
			const before = visible.filter((index) => index <= position).length - 1;
			const lower = Math.max(0, before);
			const upper = Math.min(total - 1, lower + 1);
			const span = visible[upper] - visible[lower];
			position = lower + (span ? Math.max(0, Math.min(1, (position - visible[lower]) / span)) : 0);
			const declared = Number(
				window
					.getComputedStyle(this.dots[visible[0]])
					.getPropertyValue("--pg-dots-visible"),
			);
			const windowSize = Math.min(total, declared > 0 ? declared : 5);
			const start = Math.max(
				0,
				Math.min(position - Math.floor(windowSize / 2), total - windowSize),
			);

			const activeIndex = Math.max(
				0,
				Math.min(Math.round(position), total - 1),
			);
			const firstShown = Math.round(start);
			const lastShown = firstShown + windowSize - 1;

			// Class writes only when something actually changed: this runs on
			// every scroll frame and toggling aria on 44 dots each time would be
			// both wasteful and noisy for screen readers.
			if (
				activeIndex !== this.renderedActive ||
				firstShown !== this.renderedFirst
			) {
				this.dots.forEach((dot, index) => {
					const i = visible.indexOf(index);
					const isActive = i === activeIndex;
					const isOutside = i === -1 || i < firstShown || i > lastShown;
					const isEdge =
						!isActive &&
						!isOutside &&
						((i === firstShown && firstShown > 0) ||
							(i === lastShown && lastShown < total - 1));

					dot.classList.toggle("is-active", isActive);
					dot.classList.toggle("is-outside", isOutside);
					dot.classList.toggle("is-edge", isEdge);
					dot.setAttribute("aria-current", isActive ? "true" : "false");
				});

				this.renderedActive = activeIndex;
				this.renderedFirst = firstShown;
			}

			if (!this.dotsTrack) return;

			// offsetLeft, not getBoundingClientRect: the dots carry scale()
			// transforms and the track itself is translated, both of which would
			// corrupt a rect-based measurement.
			const pitch =
				total > 1 ? this.dots[visible[1]].offsetLeft - this.dots[visible[0]].offsetLeft : 0;

			this.dotsTrack.style.transform =
				pitch > 0 ? "translateX(" + -start * pitch + "px)" : "";
		}

		scrollThumbIntoView(index, behavior) {
			const thumb = this.thumbs[index];
			const scroller = this.thumbScroller;
			if (!thumb || !scroller) return;

			const scrollerRect = scroller.getBoundingClientRect();
			const thumbRect = thumb.getBoundingClientRect();

			if (scroller.scrollHeight > scroller.clientHeight + 1) {
				const delta =
					thumbRect.top -
					scrollerRect.top -
					(scroller.clientHeight - thumbRect.height) / 2;
				scroller.scrollTo({
					top: scroller.scrollTop + delta,
					behavior: behavior,
				});
			} else if (scroller.scrollWidth > scroller.clientWidth + 1) {
				const delta =
					thumbRect.left -
					scrollerRect.left -
					(scroller.clientWidth - thumbRect.width) / 2;
				scroller.scrollTo({
					left: scroller.scrollLeft + delta,
					behavior: behavior,
				});
			}
		}

		updateArrows() {
			if (!this.prevButton || !this.nextButton) return;

			if (!this.isCarousel) {
				this.prevButton.disabled = true;
				this.nextButton.disabled = true;
				return;
			}

			const maxScroll = this.track.scrollWidth - this.track.clientWidth;
			this.prevButton.disabled = this.track.scrollLeft <= 1;
			this.nextButton.disabled = this.track.scrollLeft >= maxScroll - 1;
		}

		/** Called by the variant pickers (global.js) when the variant changes. */
		selectMedia(mediaId) {
			if (mediaId === null || mediaId === undefined) return;
			this.preferredMediaId = String(mediaId);

			// On mobile both layouts restart at the first media of the new
			// variant, so the shopper always begins at the top of that set
			// rather than somewhere in the middle of the strip.
			if (!this.isDesktop) {
				this.goToVariantStart();
				return;
			}

			const id = String(mediaId);
			const index = this.slides.findIndex(
				(slide) => slide.dataset.galleryMediaId === id,
			);

			if (index === -1) return;

			this.goTo(index, "smooth");
		}
	}

	if (!customElements.get("product-gallery")) {
		customElements.define("product-gallery", ProductGallery);
	}
})();
